import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { intl } from '../../../../i18n'
import store from '../../../../store'
import { APIResponse } from '../../../../lib/commons/api'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { requireSave } from '../../../../store/requiredSaveData'
import {
  fetchProgramList,
  fetchProgramManagementData,
  fetchProjectReports,
  saveProgramManagementDeltaBatch,
} from '../../../../infrastructure/api/program-management'
import {
  AgGridTreeHelper,
  getChildren,
} from '../../../containers/BulkSheetView/lib/tree'
import { focusRow } from '../../../containers/BulkSheetView/lib/gridApi'
import {
  Program,
  ProgramManagementResponse,
  ProgramManagementRow,
  ProjectForSelection,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'
import { programItemRowService } from '../programItemRowService'
import { aggregateTreeData } from './aggregateTreeData'
import { buildDeltaBatchRequest } from './buildDeltaBatch'
import { generateUuid } from '../../../../utils/uuids'
import { ProjectFormModel } from '../../ProjectV2/model'
import { ProjectDetail } from '../../../../infrastructure/api/project'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

const mapResponseToRow = (
  source: ProgramManagementResponse
): ProgramManagementRow => ({
  uuid: source.uuid,
  lockVersion: source.lockVersion,
  treeValue: [],
  type: source.type,
  groupUuid: source.groupUuid,
  projectUuid: source.projectUuid,
  wbsItemUuid: source.wbsItemUuid,
  iconUrl: source.iconUrl,
  code: source.code,
  displayName: source.displayName,
  status: source.status,
  projectManager: source.projectManager,
  scheduledDate: source.scheduledDate,
  progress: source.progress,
  resource: source.resource,
  profitLoss: source.profitLoss,
  projectPlanCumulation: source.projectPlanCumulation,
})

// プロジェクト単票ダイアログの入力値から、PROJECT行の表示用フィールドを作る。
const projectFormModelToRowFields = (
  formModel: ProjectFormModel
): Pick<
  ProgramManagementRow,
  | 'code'
  | 'displayName'
  | 'iconUrl'
  | 'status'
  | 'projectManager'
  | 'scheduledDate'
> => ({
  code: formModel.code,
  displayName: formModel.displayName,
  iconUrl: formModel.iconUrl,
  status: formModel.status,
  projectManager: formModel.projectManager
    ? {
        uuid: formModel.projectManager.uuid,
        name: formModel.projectManager.name,
        iconUrl: formModel.projectManager.iconUrl,
      }
    : undefined,
  scheduledDate: {
    startDate: formModel.scheduledDate.startDate
      ? dateV2Service.fromVoToApiRequest(formModel.scheduledDate.startDate)
      : undefined,
    endDate: formModel.scheduledDate.endDate
      ? dateV2Service.fromVoToApiRequest(formModel.scheduledDate.endDate)
      : undefined,
  },
})

export type UseProgramManagementDataReturn = {
  data: ProgramManagementRow[]
  setData: (data: ProgramManagementRow[]) => void
  setDataAfterEditing: (data: ProgramManagementRow[]) => void
  originalData: Map<string, ProgramManagementRow>
  deletedRows: ProgramManagementRow[]
  loading: boolean
  programs: Program[]
  addGroupRow: (gridApi: GridApi) => void
  addChildGroupRow: (gridApi: GridApi, parentRow: ProgramManagementRow) => void
  addProjectRows: (
    gridApi: GridApi,
    parentRow: ProgramManagementRow,
    projects: ProjectForSelection[]
  ) => Promise<void>
  addNewProjectRow: (
    gridApi: GridApi,
    parentRow: ProgramManagementRow,
    formModel: ProjectFormModel
  ) => void
  updateProjectRow: (
    rowUuid: string,
    formModel: ProjectFormModel,
    original?: ProjectDetail
  ) => void
  deleteRows: (rows: ProgramManagementRow[]) => void
  hasChanged: () => boolean
  save: (programUuid: string) => Promise<APIResponse>
  reload: () => Promise<void>
  refreshAggregation: (rows?: ProgramManagementRow[]) => void
}

export const useProgramManagementData = (
  selectedProgramUuid: string | undefined,
  setSelectedProgramUuid: (uuid: string | undefined) => void,
  code?: string
): UseProgramManagementDataReturn => {
  const [data, setData] = useState<ProgramManagementRow[]>([])
  const [loading, setLoading] = useState(false)
  const [programs, setPrograms] = useState<Program[]>([])
  const [deletedRows, setDeletedRows] = useState<ProgramManagementRow[]>([])
  const [originalData, setOriginalData] = useState<
    Map<string, ProgramManagementRow>
  >(new Map())
  const originalDataRef = useRef<ProgramManagementResponse | undefined>(
    undefined
  )
  const programsLoadedRef = useRef(false)

  // マウント時にプログラム一覧を取得
  useEffect(() => {
    const loadPrograms = async () => {
      const programList = await fetchProgramList()
      setPrograms(programList)
      programsLoadedRef.current = true

      if (code) {
        const matched = programList.find(p => p.code === code)
        if (matched) {
          setSelectedProgramUuid(matched.uuid)
        } else {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              text: intl.formatMessage({
                id: 'programManagement.programNotFound',
              }),
            })
          )
        }
      }
    }
    loadPrograms()
  }, [code, setSelectedProgramUuid])

  const fetchData = useCallback(async (programUuid: string) => {
    setLoading(true)
    try {
      const response = await fetchProgramManagementData(programUuid)
      originalDataRef.current = response
      const rows = AgGridTreeHelper.convert(response, mapResponseToRow)
      const aggregated = aggregateTreeData(rows)
      setData(aggregated)
      // originalData を保持
      const originalMap = new Map<string, ProgramManagementRow>()
      for (const row of aggregated) {
        originalMap.set(row.uuid, _.cloneDeep(row))
      }
      setOriginalData(originalMap)
      setDeletedRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  // プログラム選択時にデータを取得
  useEffect(() => {
    if (!selectedProgramUuid) return
    fetchData(selectedProgramUuid)
  }, [selectedProgramUuid, fetchData])

  const reload = useCallback(async () => {
    if (!selectedProgramUuid) return
    await fetchData(selectedProgramUuid)
  }, [selectedProgramUuid, fetchData])

  const setDataAfterEditing = useCallback((newData: ProgramManagementRow[]) => {
    setData(newData)
    store.dispatch(requireSave())
  }, [])

  const refreshAggregation = useCallback(
    (rows?: ProgramManagementRow[]) => {
      const target = rows ?? data
      const aggregated = aggregateTreeData(target)
      setData(aggregated)
    },
    [data]
  )

  const existingCodes = useMemo(
    () => data.filter(r => r.code).map(r => r.code!),
    [data]
  )

  const addGroupRow = useCallback(
    (gridApi: GridApi) => {
      const newRow = programItemRowService.createNewGroupRow(existingCodes)
      // PROGRAM行の直下（treeValue にPROGRAM行のuuidを含める）
      const programRow = data.find(
        r => r.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM
      )
      if (programRow) {
        newRow.treeValue = [...programRow.treeValue, newRow.uuid]
      } else {
        newRow.treeValue = [newRow.uuid]
      }
      const newData = [...data, newRow]
      // 追加直後のグループ行にも集計（空グループ → STANDBY）を反映する
      setDataAfterEditing(aggregateTreeData(newData))
      setTimeout(() => focusRow(gridApi, newRow.uuid), 100)
    },
    [data, existingCodes, setDataAfterEditing]
  )

  const addChildGroupRow = useCallback(
    (gridApi: GridApi, parentRow: ProgramManagementRow) => {
      const newRow = programItemRowService.createNewGroupRow(existingCodes)
      newRow.treeValue = [...parentRow.treeValue, newRow.uuid]
      const parentChildren = getChildren(data, parentRow)
      let insertIndex: number
      if (parentChildren.length > 0) {
        const lastChild = parentChildren[parentChildren.length - 1]
        insertIndex = data.indexOf(lastChild) + 1
      } else {
        insertIndex = data.indexOf(parentRow) + 1
      }
      const newData = [
        ...data.slice(0, insertIndex),
        newRow,
        ...data.slice(insertIndex),
      ]
      // 追加直後のグループ行にも集計（空グループ → STANDBY）を反映する
      setDataAfterEditing(aggregateTreeData(newData))
      setTimeout(() => focusRow(gridApi, newRow.uuid), 100)
    },
    [data, existingCodes, setDataAfterEditing]
  )

  const addProjectRows = useCallback(
    async (
      gridApi: GridApi,
      parentRow: ProgramManagementRow,
      projects: ProjectForSelection[]
    ) => {
      // プロジェクトのレポートデータを取得（予定日、進捗、リソース、損益、配下のWORKGROUP/PROCESS）
      const projectUuids = projects.map(p => p.uuid)
      const reportDtos = await fetchProjectReports(projectUuids)

      // レポートDTOをprojectUuidでマッピング
      const reportByProjectUuid = new Map(
        reportDtos.map(dto => [dto.projectUuid, dto])
      )

      // PROJECT行と配下のWORKGROUP/PROCESS行をフラットに展開
      const allNewRows: ProgramManagementRow[] = []

      for (const project of projects) {
        const projectRowUuid = generateUuid()
        const report = reportByProjectUuid.get(project.uuid)

        const projectRow: ProgramManagementRow = {
          uuid: projectRowUuid,
          lockVersion: 0,
          treeValue: [...parentRow.treeValue, projectRowUuid],
          type: PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT,
          projectUuid: project.uuid,
          wbsItemUuid: report?.wbsItemUuid,
          code: report?.code ?? project.code,
          displayName: report?.displayName ?? project.displayName,
          iconUrl: report?.iconUrl ?? project.iconUrl,
          status: report?.status,
          projectManager: report?.projectManager,
          scheduledDate: report?.scheduledDate,
          progress: report?.progress,
          resource: report?.resource,
          profitLoss: report?.profitLoss,
          added: true,
          edited: true,
        }
        allNewRows.push(projectRow)

        // 配下のWORKGROUP/PROCESS行を再帰的にフラット化
        if (report?.children) {
          const flattenChildren = (
            children: typeof reportDtos,
            parentTreeValue: string[]
          ) => {
            for (const child of children) {
              const childRow = mapResponseToRow(child)
              childRow.treeValue = [...parentTreeValue, childRow.uuid]
              allNewRows.push(childRow)
              if (child.children && child.children.length > 0) {
                flattenChildren(child.children, childRow.treeValue)
              }
            }
          }
          flattenChildren(report.children, projectRow.treeValue)
        }
      }

      // 親の子の末尾に追加
      const parentChildren = getChildren(data, parentRow)
      let insertIndex: number
      if (parentChildren.length > 0) {
        const lastChild = parentChildren[parentChildren.length - 1]
        insertIndex = data.indexOf(lastChild) + 1
      } else {
        insertIndex = data.indexOf(parentRow) + 1
      }
      const newData = [
        ...data.slice(0, insertIndex),
        ...allNewRows,
        ...data.slice(insertIndex),
      ]
      const aggregated = aggregateTreeData(newData)
      setDataAfterEditing(aggregated)
      if (allNewRows.length > 0) {
        setTimeout(() => focusRow(gridApi, allNewRows[0].uuid), 100)
      }
    },
    [data, setDataAfterEditing]
  )

  // 新規プロジェクトの下書きから PROJECT 行を1行追加する（DB未保存。projectDraft を保持）。
  // ダイアログ確定（onComplete）は遅延して呼ばれるため、関数型更新で最新 data を参照する。
  const addNewProjectRow = useCallback(
    (
      gridApi: GridApi,
      parentRow: ProgramManagementRow,
      formModel: ProjectFormModel
    ) => {
      const rowUuid = generateUuid()
      setData(prev => {
        const newRow: ProgramManagementRow = {
          uuid: rowUuid,
          lockVersion: 0,
          treeValue: [...parentRow.treeValue, rowUuid],
          type: PROGRAM_MANAGEMENT_ROW_TYPE.PROJECT,
          // 新規プロジェクトの uuid は単票で採番済み（form model.uuid）。
          projectUuid: formModel.uuid,
          ...projectFormModelToRowFields(formModel),
          projectDraft: formModel,
          isNewProject: true,
          added: true,
          edited: true,
        }
        const parentChildren = getChildren(prev, parentRow)
        const insertIndex =
          parentChildren.length > 0
            ? prev.indexOf(parentChildren[parentChildren.length - 1]) + 1
            : prev.indexOf(parentRow) + 1
        const newData = [
          ...prev.slice(0, insertIndex),
          newRow,
          ...prev.slice(insertIndex),
        ]
        return aggregateTreeData(newData)
      })
      store.dispatch(requireSave())
      setTimeout(() => focusRow(gridApi, rowUuid), 100)
    },
    []
  )

  // プロジェクト単票ダイアログでの編集を行へ反映する。
  // original を渡した場合は既存プロジェクトの更新（保存時に delta 算出）。
  const updateProjectRow = useCallback(
    (
      rowUuid: string,
      formModel: ProjectFormModel,
      original?: ProjectDetail
    ) => {
      setData(prev =>
        aggregateTreeData(
          prev.map(row => {
            if (row.uuid !== rowUuid) return row
            return {
              ...row,
              ...projectFormModelToRowFields(formModel),
              projectDraft: formModel,
              projectOriginal: original ?? row.projectOriginal,
              edited: true,
            }
          })
        )
      )
      store.dispatch(requireSave())
    },
    []
  )

  const deleteRows = useCallback(
    (rows: ProgramManagementRow[]) => {
      const deletedUuids = new Set(rows.map(r => r.uuid))
      // 非added行を deletedRows に追加
      const newDeletedRows = rows.filter(r => !r.added)
      setDeletedRows(prev => [...prev, ...newDeletedRows])
      // データから削除（子孫も含む）
      const newData = data.filter(
        r =>
          !deletedUuids.has(r.uuid) &&
          !r.treeValue.some(v => deletedUuids.has(v) && v !== r.uuid)
      )
      const aggregated = aggregateTreeData(newData)
      setDataAfterEditing(aggregated)
    },
    [data, setDataAfterEditing]
  )

  const hasChanged = useCallback(() => {
    return data.some(v => v.added || v.edited) || deletedRows.length > 0
  }, [data, deletedRows])

  const save = useCallback(
    (programUuid: string): Promise<APIResponse> => {
      const request = buildDeltaBatchRequest(
        programUuid,
        data,
        originalData,
        deletedRows
      )
      return saveProgramManagementDeltaBatch(programUuid, request)
    },
    [data, originalData, deletedRows]
  )

  return {
    data,
    setData,
    setDataAfterEditing,
    originalData,
    deletedRows,
    loading,
    programs,
    addGroupRow,
    addChildGroupRow,
    addProjectRows,
    addNewProjectRow,
    updateProjectRow,
    deleteRows,
    hasChanged,
    save,
    reload,
    refreshAggregation,
  }
}
